import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Send, CheckCircle, Linkedin } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';

const ContactPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // In production, this would send to backend
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'nl' ? 'Terug' : 'Back'}
          </button>

          {/* Hero */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'nl' ? 'Neem Contact Op' : 'Get in Touch'}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {language === 'nl' 
                ? 'Heb je vragen over AI in HR? We helpen je graag verder.'
                : 'Have questions about AI in HR? We\'re happy to help.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Contact Info */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {language === 'nl' ? 'Contactgegevens' : 'Contact Information'}
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <img 
                      src="/dominique-photo.png" 
                      alt="Dominique Dejonghe"
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Dominique Dejonghe</p>
                      <p className="text-sm text-blue-500">AI for HR change and transformation agent</p>
                      <div className="mt-2 space-y-1">
                        <a href="tel:+32498900947" className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-500">
                          <Phone className="h-3 w-3" /> +32 498 900 947
                        </a>
                        <a href="mailto:dominique@humanistix.be" className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-500">
                          <Mail className="h-3 w-3" /> dominique@humanistix.be
                        </a>
                        <a 
                          href="https://www.linkedin.com/in/ddejonghe" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-500"
                        >
                          <Linkedin className="h-3 w-3" /> LinkedIn
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <img 
                      src="/luc-photo.png" 
                      alt="Luc Langeraert"
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Luc Langeraert</p>
                      <p className="text-sm text-blue-500">Digital HR & Change</p>
                      <div className="mt-2 space-y-1">
                        <a href="tel:+32494561819" className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-500">
                          <Phone className="h-3 w-3" /> +32 494 56 18 19
                        </a>
                        <a href="mailto:luc@humanistix.be" className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-500">
                          <Mail className="h-3 w-3" /> luc@humanistix.be
                        </a>
                        <a 
                          href="https://www.linkedin.com/in/luclangeraert" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-500"
                        >
                          <Linkedin className="h-3 w-3" /> LinkedIn
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="h-4 w-4 text-blue-500" />
                    <span>Antwerpen - Gent - Brussel</span>
                  </div>
                </div>
              </div>
              
              {/* Book a call CTA */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-700 rounded-2xl p-6 text-white">
                <h3 className="text-lg font-bold mb-2">
                  {language === 'nl' ? 'Liever direct praten?' : 'Prefer to talk directly?'}
                </h3>
                <p className="text-blue-100 text-sm mb-4">
                  {language === 'nl' 
                    ? 'Boek een gratis 30-minuten gesprek.'
                    : 'Book a free 30-minute call.'}
                </p>
                <a 
                  href="https://app.cal.eu/humanistix/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-blue-50 transition-colors"
                >
                  {language === 'nl' ? 'Boek Gesprek' : 'Book Call'}
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {language === 'nl' ? 'Bericht Verzonden!' : 'Message Sent!'}
                  </h3>
                  <p className="text-gray-600">
                    {language === 'nl' 
                      ? 'We nemen zo snel mogelijk contact met je op.'
                      : 'We\'ll get back to you as soon as possible.'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    {language === 'nl' ? 'Stuur een Bericht' : 'Send a Message'}
                  </h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {language === 'nl' ? 'Naam' : 'Name'} *
                    </label>
                    <Input 
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <Input 
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {language === 'nl' ? 'Bedrijf' : 'Company'}
                    </label>
                    <Input 
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {language === 'nl' ? 'Bericht' : 'Message'} *
                    </label>
                    <textarea
                      required
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                    />
                  </div>
                  
                  <Button type="submit" className="w-full bg-blue-500 hover:bg-blue-600">
                    <Send className="h-4 w-4 mr-2" />
                    {language === 'nl' ? 'Verstuur Bericht' : 'Send Message'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ContactPage;
